import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { CategoryGroups, groupByCategory } from "@/components/common/CategoryGroups";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { FloatingButton } from "@/components/common/FloatingButton";
import { Loader } from "@/components/common/Loader";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useCurrency } from "@/features/accounts/hooks/useCurrency";
import { FALLBACK_CATEGORY_CODE } from "@/features/categories/api/categoriesApi";
import { useProductCategories } from "@/features/categories/hooks/useCategories";
import { ProductList } from "@/features/products/components/ProductList";
import { useDeleteProduct, useProducts } from "@/features/products/hooks/useProducts";
import ProductFormPage from "@/features/products/ProductFormPage";
import type { Product } from "@/features/products/types";
import { useRecipes } from "@/features/recipes/hooks/useRecipes";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { toErrorMessage } from "@/lib/swr";

export default function ProductsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Product | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const isDesktop = useIsDesktop();
  const toast = useToast();

  const { currency } = useCurrency();
  const { categories } = useProductCategories();
  const { recipes } = useRecipes();
  const { remove, isDeleting } = useDeleteProduct();

  const { products: fetched, isLoading, error, refresh } = useProducts();
  const products = fetched as unknown as Product[];

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return products;
    return products.filter(
      (product) => product.name.toLowerCase().includes(term) || product.description?.toLowerCase().includes(term),
    );
  }, [products, searchTerm]);

  /** Categories replace paging here: they cut the catalogue into short lists. */
  const groups = useMemo(() => groupByCategory(filtered, categories, FALLBACK_CATEGORY_CODE), [filtered, categories]);

  /** Named so the confirmation can say what the deletion takes down with it. */
  const recipeOf = (productId: string) => recipes.find((recipe) => recipe.productId === productId);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const name = pendingDelete.name;
    setDeleteError(null);
    try {
      const deleted = await remove(pendingDelete.id);
      toast.success(deleted.deletedRecipeId ? `« ${name} » supprimé, ainsi que sa recette.` : `« ${name} » supprimé.`);
      setPendingDelete(null);
    } catch (e) {
      const message = toErrorMessage(e) ?? "Le serveur n’a pas précisé la cause.";
      setDeleteError(`Suppression impossible. ${message} Vérifiez la cause puis réessayez.`);
    }
  };

  if (isCreating || editing) {
    return (
      <ProductFormPage
        product={editing ?? undefined}
        onBack={() => {
          setIsCreating(false);
          setEditing(null);
          refresh();
        }}
      />
    );
  }

  const pendingRecipe = pendingDelete ? recipeOf(pendingDelete.id) : undefined;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Produits"
        subtitle="Le catalogue de ce que vous vendez"
        action={
          isDesktop ? (
            <Button type="button" onClick={() => setIsCreating(true)} size="lg">
              <Plus />
              Nouveau produit
            </Button>
          ) : null
        }
      />

      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Rechercher un produit…"
      />

      {isLoading && <Loader />}

      {!isLoading && error && <ErrorState message={error} onRetry={() => refresh()} />}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          title={searchTerm ? "Aucun produit ne correspond" : "Aucun produit"}
          hint={searchTerm ? "Essayez un autre nom." : "Ajoutez ce que vous vendez pour pouvoir encaisser."}
          action={
            searchTerm ? (
              <Button type="button" variant="outline" onClick={() => setSearchTerm("")}>
                <X />
                Effacer la recherche
              </Button>
            ) : (
              <Button type="button" onClick={() => setIsCreating(true)}>
                <Plus />
                Nouveau produit
              </Button>
            )
          }
        />
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <CategoryGroups groups={groups} countLabel="produit">
          {(items) => (
            <ProductList products={items} currency={currency} onEdit={setEditing} onDelete={setPendingDelete} />
          )}
        </CategoryGroups>
      )}

      {!isDesktop && <FloatingButton icon={Plus} ariaLabel="Nouveau produit" onClick={() => setIsCreating(true)} />}

      {pendingDelete && (
        <ConfirmDialog
          title={`Supprimer « ${pendingDelete.name} » ?`}
          description="Il disparaîtra du catalogue, de la caisse et de votre stock marchand."
          details={
            pendingRecipe
              ? `Sa recette « ${pendingRecipe.name} » et les productions qui en découlent seront supprimées aussi. Les ventes déjà encaissées sont conservées comme trace.`
              : "Les ventes déjà encaissées sont conservées comme trace."
          }
          confirmLabel="Supprimer"
          isPending={isDeleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteError(null);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
}
